import React, { useContext } from 'react'
import merge from 'lodash/merge'
import { GenericFetchContextType } from './types'

/**
 * this is a convenience hook to get data from an array of contexts
 * @param {Array<React.Context>} - contexts - the array of contexts to use data from
 * @return {Array<GenericFetchContextType>} - an array of all the context
 */
export function useContexts(
  contexts: Array<React.Context<GenericFetchContextType>>
): Array<GenericFetchContextType> {
  const contextData = []
  for (let contextOn = 0; contextOn < contexts.length; contextOn++) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    contextData.push(useContext(contexts[contextOn]))
  }
  return contextData
}

/**
 * @type reduxCookFunctionType
 * The specific function signature for a function that "cooks" context data to data in the redux form
 */
type ReduxCookFunctionType<T> = (context: T) => T

/**
 * @interface ContextCookerType
 * this interface defines a single context and an array of cook functions to convert data from the context to redux form
 */
export type ContextCookerType = {
  context: React.Context<GenericFetchContextType>
  cookers: Array<ReduxCookFunctionType<Record<string, unknown>>>
}

export type SelectorType = (state: Record<string, unknown>) => unknown

/**
 * this is a convenience hook to get data from an array of contexts using an array of cook functions to cook
 * the data into a redux form compatible with existing Signal selectors
 * @param {Array<ContextCookerType>} - contextCookers - the array of contexts to pull data from and "cook
 *   function(s)" to un the contexts through to convert them to redux form
 * @return {Object} - the cooked data a form that can be passed to a redux selector
 */
export function useContextsInReduxForm(
  contextCookers: Array<ContextCookerType>
): Record<string, unknown> {
  const contextData = useContexts(contextCookers.map(({ context }) => context))
  return contextCookers.reduce(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (selectorState, { context, cookers }, contextOn) =>
      cookers.reduce(
        (_context, cooker) => merge(cooker(contextData[contextOn]), _context),
        selectorState
      ),
    {}
  )
}
